// Textos del widget en 10 idiomas.
// El idioma inicial viene de la config del cliente; el visitante puede cambiarlo
// desde el selector del panel y se recuerda en localStorage.
// Cualquier clave que falte en un idioma cae al español (idioma base).

export type Lang = "es" | "en" | "fr" | "de" | "pt" | "it" | "zh" | "ja" | "ar" | "ru";

export interface Strings {
  [key: string]: string;
}

/** Nombre nativo de cada idioma para el selector del panel. */
export const LANG_NAMES: Record<Lang, string> = {
  es: "Español",
  en: "English",
  fr: "Français",
  de: "Deutsch",
  pt: "Português",
  it: "Italiano",
  zh: "中文",
  ja: "日本語",
  ar: "العربية",
  ru: "Русский",
};

/** Locale BCP-47 para síntesis y reconocimiento de voz. */
export const LOCALES: Record<Lang, string> = {
  es: "es-ES",
  en: "en-US",
  fr: "fr-FR",
  de: "de-DE",
  pt: "pt-BR",
  it: "it-IT",
  zh: "zh-CN",
  ja: "ja-JP",
  ar: "ar-SA",
  ru: "ru-RU",
};

/** Idiomas que se escriben de derecha a izquierda. */
export const RTL: Lang[] = ["ar"];

const es: Strings = {
  easyRead: "Lectura fácil (IA)",
  aiSummary: "Resumen con IA",
  aiLoading: "Generando con IA…",
  aiError: "La IA no está disponible ahora.",
  title: "Accesibilidad",
  open: "Abrir menú de accesibilidad",
  close: "Cerrar",
  moveSide: "Mover el panel de lado",
  hideWidget: "Ocultar widget",
  profiles: "Perfiles de accesibilidad",
  navigation: "Ajustes de navegación",
  colors: "Ajustes de color",
  content: "Ajustes de contenido",
  pMotor: "Motriz",
  pBlind: "Ceguera",
  pColorblind: "Daltonismo",
  pDyslexia: "Dislexia",
  pLowVision: "Baja visión",
  pCognitive: "Cognitivo",
  pEpilepsy: "Epilepsia",
  pAdhd: "TDAH",
  srMode: "Lectura al enfocar",
  keyboardNav: "Navegación por teclado",
  mousegrid: "Cuadrícula de ratón",
  smartNav: "Navegación inteligente",
  tts: "Lector de texto",
  voice: "Comandos de voz",
  mono: "Monocromo",
  darkhc: "Contraste alto oscuro",
  brighthc: "Contraste alto claro",
  lowsat: "Saturación baja",
  highsat: "Saturación alta",
  contrastMode: "Modo contraste",
  customColor: "Color personalizado",
  customColorDesc: "Cambia los colores del sitio",
  bgs: "Fondos",
  headings: "Títulos",
  contents: "Contenidos",
  resetColors: "Restablecer colores",
  fontSizing: "Tamaño de fuente",
  fontSizingDesc: "Aumenta y reduce el tamaño del texto",
  fontSize: "Tamaño",
  lineSp: "Interlineado",
  wordSp: "Entre palabras",
  letterSp: "Entre letras",
  resetFont: "Restablecer fuente",
  cursor: "Cursor",
  cursorDesc: "Agranda el cursor y cambia su color",
  cursorWhite: "BLANCO",
  cursorBlack: "NEGRO",
  blinks: "Bloquear parpadeos",
  magnifier: "Lupa",
  readableFont: "Fuente legible",
  imgDesc: "Descripción de imágenes",
  links: "Resaltar enlaces",
  readableMode: "Modo lectura",
  textMag: "Ampliador de texto",
  pageStructure: "Estructura de página",
  muteMedia: "Silenciar medios",
  pageSummary: "Resumen de página",
  readFocus: "Foco de lectura",
  readGuide: "Guía de lectura",
  dict: "Diccionario",
  vkb: "Teclado virtual",
  turnOff: "Apagar",
  turnOn: "Encender",
  statement: "Declaración de accesibilidad",
  feedback: "Enviar comentarios",
  resetAll: "Restablecer todo",
  structHeadings: "Encabezados",
  structLandmarks: "Regiones",
  structLinks: "Enlaces",
  emptyList: "No se encontró ninguno en esta página.",
  summaryTitle: "Resumen de la página",
  dictHint: "Selecciona una palabra en la página para buscar su definición.",
  dictSearching: "Buscando…",
  dictNotFound: "No se encontró definición.",
  noDesc: "Sin descripción",
  voiceOn: "Voz activa: di «bajar», «subir», «inicio», «fin», «leer», «parar»",
  voiceUnsupported: "Este navegador no soporta comandos de voz.",
  ttsUnsupported: "Este navegador no soporta síntesis de voz.",
  ttsHint: "Haz clic sobre cualquier texto para escucharlo.",
  gridHint: "Pulsa 1-9 para acercar · Enter para hacer clic · Esc para salir",
  kbdHint: "H encabezados · L enlaces · B botones · G regiones",
  smartHint: "Usa ← ↑ ↓ → para moverte entre elementos y Enter para activar.",
  offNote: "La herramienta está apagada. Pulsa «Encender» abajo para reactivar tus ajustes.",
  statementText:
    "Este sitio incluye una herramienta de preferencias de accesibilidad que permite a cada visitante ajustar la presentación (tamaño de texto, contraste, espaciado, guías de lectura, lectura en voz alta y más). Es una capa de comodidad para el usuario: complementa, pero no sustituye, la accesibilidad del código y el contenido del sitio conforme a WCAG 2.2. Si encuentras una barrera de acceso, contáctanos con el enlace «Enviar comentarios».",
};

const en: Strings = {
  easyRead: "Easy Read (AI)",
  aiSummary: "AI summary",
  aiLoading: "Generating with AI…",
  aiError: "AI is not available right now.",
  title: "Accessibility",
  open: "Open accessibility menu",
  close: "Close",
  moveSide: "Move panel to other side",
  hideWidget: "Hide widget",
  profiles: "Accessibility Profiles",
  navigation: "Navigation Adjustment",
  colors: "Color Adjustment",
  content: "Content Adjustment",
  pMotor: "Motor",
  pBlind: "Blindness",
  pColorblind: "Color blindness",
  pDyslexia: "Dyslexia",
  pLowVision: "Low vision",
  pCognitive: "Cognitive",
  pEpilepsy: "Epilepsy",
  pAdhd: "ADHD",
  srMode: "Read on focus",
  keyboardNav: "Keyboard Navigation",
  mousegrid: "Mousegrid",
  smartNav: "Smart Navigation",
  tts: "Text Reader",
  voice: "Voice Commands",
  mono: "Monochrome",
  darkhc: "Dark High-Contrast",
  brighthc: "Bright High-Contrast",
  lowsat: "Low saturation",
  highsat: "High saturation",
  contrastMode: "Contrast Mode",
  customColor: "Custom Color",
  customColorDesc: "Change the site's colors",
  bgs: "Backgrounds",
  headings: "Headings",
  contents: "Contents",
  resetColors: "Reset colors",
  fontSizing: "Font Sizing",
  fontSizingDesc: "Increase and decrease the font size",
  fontSize: "Font Size",
  lineSp: "Line Spacing",
  wordSp: "Word Spacing",
  letterSp: "Letter Spacing",
  resetFont: "Reset font",
  cursor: "Cursor",
  cursorDesc: "Enlarge the cursor and change its color",
  cursorWhite: "WHITE",
  cursorBlack: "BLACK",
  blinks: "Blinks Blocking",
  magnifier: "Magnifier",
  readableFont: "Readable Font",
  imgDesc: "Image Descriptions",
  links: "Highlight Links",
  readableMode: "Readable Mode",
  textMag: "Text Magnifier",
  pageStructure: "Page Structure",
  muteMedia: "Mute Media",
  pageSummary: "Page Summary",
  readFocus: "Read focus",
  readGuide: "Reading guide",
  dict: "Dictionary",
  vkb: "Virtual Keyboard",
  turnOff: "Turn Off",
  turnOn: "Turn On",
  statement: "Accessibility Statement",
  feedback: "Send Feedback",
  resetAll: "Reset all",
  structHeadings: "Headings",
  structLandmarks: "Landmarks",
  structLinks: "Links",
  emptyList: "None found on this page.",
  summaryTitle: "Page summary",
  dictHint: "Select a word on the page to look up its definition.",
  dictSearching: "Searching…",
  dictNotFound: "No definition found.",
  noDesc: "No description",
  voiceOn: "Voice on: say “down”, “up”, “top”, “bottom”, “read”, “stop”",
  voiceUnsupported: "This browser does not support voice commands.",
  ttsUnsupported: "This browser does not support speech synthesis.",
  ttsHint: "Click any text to hear it.",
  gridHint: "Press 1-9 to zoom in · Enter to click · Esc to exit",
  kbdHint: "H headings · L links · B buttons · G landmarks",
  smartHint: "Use ← ↑ ↓ → to move between elements and Enter to activate.",
  offNote: "The tool is turned off. Press “Turn On” below to re-enable your adjustments.",
  statementText:
    "This site includes an accessibility preferences tool that lets each visitor adjust presentation (text size, contrast, spacing, reading guides, read-aloud and more). It is a user-comfort layer: it complements, but does not replace, accessible code and content per WCAG 2.2. If you find an access barrier, contact us via the “Send Feedback” link.",
};

const fr: Strings = {
  easyRead: "Lecture facile (IA)",
  aiSummary: "Résumé par IA",
  aiLoading: "Génération par IA…",
  aiError: "L'IA n'est pas disponible pour le moment.",
  title: "Accessibilité",
  open: "Ouvrir le menu d'accessibilité",
  close: "Fermer",
  moveSide: "Déplacer le panneau de côté",
  hideWidget: "Masquer le widget",
  profiles: "Profils d'accessibilité",
  navigation: "Réglages de navigation",
  colors: "Réglages des couleurs",
  content: "Réglages du contenu",
  pMotor: "Moteur",
  pBlind: "Cécité",
  pColorblind: "Daltonisme",
  pDyslexia: "Dyslexie",
  pLowVision: "Basse vision",
  pCognitive: "Cognitif",
  pEpilepsy: "Épilepsie",
  pAdhd: "TDAH",
  srMode: "Lecture au focus",
  keyboardNav: "Navigation au clavier",
  mousegrid: "Grille de souris",
  smartNav: "Navigation intelligente",
  tts: "Lecteur de texte",
  voice: "Commandes vocales",
  mono: "Monochrome",
  darkhc: "Contraste élevé sombre",
  brighthc: "Contraste élevé clair",
  lowsat: "Saturation faible",
  highsat: "Saturation élevée",
  contrastMode: "Mode contraste",
  customColor: "Couleur personnalisée",
  customColorDesc: "Changer les couleurs du site",
  bgs: "Arrière-plans",
  headings: "Titres",
  contents: "Contenus",
  resetColors: "Réinitialiser les couleurs",
  fontSizing: "Taille de police",
  fontSizingDesc: "Augmenter ou réduire la taille du texte",
  fontSize: "Taille",
  lineSp: "Interligne",
  wordSp: "Entre les mots",
  letterSp: "Entre les lettres",
  resetFont: "Réinitialiser la police",
  cursor: "Curseur",
  cursorDesc: "Agrandir le curseur et changer sa couleur",
  cursorWhite: "BLANC",
  cursorBlack: "NOIR",
  blinks: "Bloquer les clignotements",
  magnifier: "Loupe",
  readableFont: "Police lisible",
  imgDesc: "Description des images",
  links: "Surligner les liens",
  readableMode: "Mode lecture",
  textMag: "Agrandisseur de texte",
  pageStructure: "Structure de la page",
  muteMedia: "Couper les médias",
  pageSummary: "Résumé de la page",
  readFocus: "Focus de lecture",
  readGuide: "Guide de lecture",
  dict: "Dictionnaire",
  vkb: "Clavier virtuel",
  turnOff: "Désactiver",
  turnOn: "Activer",
  statement: "Déclaration d'accessibilité",
  feedback: "Envoyer un commentaire",
  resetAll: "Tout réinitialiser",
  structHeadings: "Titres",
  structLandmarks: "Régions",
  structLinks: "Liens",
  emptyList: "Aucun trouvé sur cette page.",
  summaryTitle: "Résumé de la page",
  dictHint: "Sélectionnez un mot sur la page pour chercher sa définition.",
  dictSearching: "Recherche…",
  dictNotFound: "Aucune définition trouvée.",
  noDesc: "Sans description",
  voiceOn: "Voix active : dites « descendre », « monter », « début », « fin », « lire », « stop »",
  voiceUnsupported: "Ce navigateur ne prend pas en charge les commandes vocales.",
  ttsUnsupported: "Ce navigateur ne prend pas en charge la synthèse vocale.",
  ttsHint: "Cliquez sur un texte pour l'écouter.",
  gridHint: "Appuyez sur 1-9 pour zoomer · Entrée pour cliquer · Échap pour quitter",
  kbdHint: "H titres · L liens · B boutons · G régions",
  smartHint: "Utilisez ← ↑ ↓ → pour vous déplacer et Entrée pour activer.",
  offNote: "L'outil est désactivé. Appuyez sur « Activer » ci-dessous pour réactiver vos réglages.",
  statementText:
    "Ce site inclut un outil de préférences d'accessibilité qui permet à chaque visiteur d'ajuster la présentation (taille du texte, contraste, espacement, guides de lecture, lecture à voix haute et plus). C'est une couche de confort : elle complète, mais ne remplace pas, l'accessibilité du code et du contenu selon WCAG 2.2. Si vous rencontrez un obstacle, contactez-nous via « Envoyer un commentaire ».",
};

const de: Strings = {
  easyRead: "Leichte Sprache (KI)",
  aiSummary: "KI-Zusammenfassung",
  aiLoading: "KI generiert…",
  aiError: "KI ist gerade nicht verfügbar.",
  title: "Barrierefreiheit",
  open: "Barrierefreiheitsmenü öffnen",
  close: "Schließen",
  moveSide: "Panel auf die andere Seite verschieben",
  hideWidget: "Widget ausblenden",
  profiles: "Barrierefreiheitsprofile",
  navigation: "Navigationseinstellungen",
  colors: "Farbeinstellungen",
  content: "Inhaltseinstellungen",
  pMotor: "Motorisch",
  pBlind: "Blindheit",
  pColorblind: "Farbenblindheit",
  pDyslexia: "Legasthenie",
  pLowVision: "Sehschwäche",
  pCognitive: "Kognitiv",
  pEpilepsy: "Epilepsie",
  pAdhd: "ADHS",
  srMode: "Vorlesen bei Fokus",
  keyboardNav: "Tastaturnavigation",
  mousegrid: "Mausraster",
  smartNav: "Intelligente Navigation",
  tts: "Textvorleser",
  voice: "Sprachbefehle",
  mono: "Monochrom",
  darkhc: "Dunkler Hochkontrast",
  brighthc: "Heller Hochkontrast",
  lowsat: "Geringe Sättigung",
  highsat: "Hohe Sättigung",
  contrastMode: "Kontrastmodus",
  customColor: "Benutzerdefinierte Farbe",
  customColorDesc: "Farben der Website ändern",
  bgs: "Hintergründe",
  headings: "Überschriften",
  contents: "Inhalte",
  resetColors: "Farben zurücksetzen",
  fontSizing: "Schriftgröße",
  fontSizingDesc: "Schriftgröße vergrößern und verkleinern",
  fontSize: "Größe",
  lineSp: "Zeilenabstand",
  wordSp: "Wortabstand",
  letterSp: "Buchstabenabstand",
  resetFont: "Schrift zurücksetzen",
  cursor: "Cursor",
  cursorDesc: "Cursor vergrößern und Farbe ändern",
  cursorWhite: "WEISS",
  cursorBlack: "SCHWARZ",
  blinks: "Blinken blockieren",
  magnifier: "Lupe",
  readableFont: "Lesbare Schrift",
  imgDesc: "Bildbeschreibungen",
  links: "Links hervorheben",
  readableMode: "Lesemodus",
  textMag: "Textvergrößerer",
  pageStructure: "Seitenstruktur",
  muteMedia: "Medien stummschalten",
  pageSummary: "Seitenzusammenfassung",
  readFocus: "Lesefokus",
  readGuide: "Leselineal",
  dict: "Wörterbuch",
  vkb: "Virtuelle Tastatur",
  turnOff: "Ausschalten",
  turnOn: "Einschalten",
  statement: "Erklärung zur Barrierefreiheit",
  feedback: "Feedback senden",
  resetAll: "Alles zurücksetzen",
  structHeadings: "Überschriften",
  structLandmarks: "Bereiche",
  structLinks: "Links",
  emptyList: "Keine auf dieser Seite gefunden.",
  summaryTitle: "Seitenzusammenfassung",
  dictHint: "Wählen Sie ein Wort auf der Seite aus, um die Definition nachzuschlagen.",
  dictSearching: "Suche…",
  dictNotFound: "Keine Definition gefunden.",
  noDesc: "Keine Beschreibung",
  voiceOn: "Sprache aktiv: sagen Sie „runter“, „hoch“, „Anfang“, „Ende“, „lesen“, „stopp“",
  voiceUnsupported: "Dieser Browser unterstützt keine Sprachbefehle.",
  ttsUnsupported: "Dieser Browser unterstützt keine Sprachausgabe.",
  ttsHint: "Klicken Sie auf einen Text, um ihn zu hören.",
  gridHint: "1-9 zum Zoomen · Enter zum Klicken · Esc zum Beenden",
  kbdHint: "H Überschriften · L Links · B Schaltflächen · G Bereiche",
  smartHint: "Mit ← ↑ ↓ → zwischen Elementen wechseln, Enter aktiviert.",
  offNote: "Das Tool ist ausgeschaltet. Drücken Sie unten „Einschalten“, um Ihre Einstellungen zu reaktivieren.",
  statementText:
    "Diese Website enthält ein Tool für Barrierefreiheits-Einstellungen, mit dem jeder Besucher die Darstellung anpassen kann (Textgröße, Kontrast, Abstände, Lesehilfen, Vorlesen und mehr). Es ist eine Komfortschicht: Sie ergänzt, ersetzt aber nicht barrierefreien Code und Inhalt gemäß WCAG 2.2. Wenn Sie auf eine Barriere stoßen, kontaktieren Sie uns über „Feedback senden“.",
};

const pt: Strings = {
  easyRead: "Leitura fácil (IA)",
  aiSummary: "Resumo com IA",
  aiLoading: "Gerando com IA…",
  aiError: "A IA não está disponível agora.",
  title: "Acessibilidade",
  open: "Abrir menu de acessibilidade",
  close: "Fechar",
  moveSide: "Mover o painel de lado",
  hideWidget: "Ocultar widget",
  profiles: "Perfis de acessibilidade",
  navigation: "Ajustes de navegação",
  colors: "Ajustes de cor",
  content: "Ajustes de conteúdo",
  pMotor: "Motor",
  pBlind: "Cegueira",
  pColorblind: "Daltonismo",
  pDyslexia: "Dislexia",
  pLowVision: "Baixa visão",
  pCognitive: "Cognitivo",
  pEpilepsy: "Epilepsia",
  pAdhd: "TDAH",
  srMode: "Leitura ao focar",
  keyboardNav: "Navegação por teclado",
  mousegrid: "Grade do mouse",
  smartNav: "Navegação inteligente",
  tts: "Leitor de texto",
  voice: "Comandos de voz",
  mono: "Monocromático",
  darkhc: "Alto contraste escuro",
  brighthc: "Alto contraste claro",
  lowsat: "Saturação baixa",
  highsat: "Saturação alta",
  contrastMode: "Modo contraste",
  customColor: "Cor personalizada",
  customColorDesc: "Mude as cores do site",
  bgs: "Fundos",
  headings: "Títulos",
  contents: "Conteúdos",
  resetColors: "Redefinir cores",
  fontSizing: "Tamanho da fonte",
  fontSizingDesc: "Aumente e diminua o tamanho do texto",
  fontSize: "Tamanho",
  lineSp: "Entrelinha",
  wordSp: "Entre palavras",
  letterSp: "Entre letras",
  resetFont: "Redefinir fonte",
  cursor: "Cursor",
  cursorDesc: "Aumente o cursor e mude sua cor",
  cursorWhite: "BRANCO",
  cursorBlack: "PRETO",
  blinks: "Bloquear piscadas",
  magnifier: "Lupa",
  readableFont: "Fonte legível",
  imgDesc: "Descrição de imagens",
  links: "Destacar links",
  readableMode: "Modo leitura",
  textMag: "Ampliador de texto",
  pageStructure: "Estrutura da página",
  muteMedia: "Silenciar mídia",
  pageSummary: "Resumo da página",
  readFocus: "Foco de leitura",
  readGuide: "Guia de leitura",
  dict: "Dicionário",
  vkb: "Teclado virtual",
  turnOff: "Desligar",
  turnOn: "Ligar",
  statement: "Declaração de acessibilidade",
  feedback: "Enviar comentários",
  resetAll: "Redefinir tudo",
  structHeadings: "Cabeçalhos",
  structLandmarks: "Regiões",
  structLinks: "Links",
  emptyList: "Nenhum encontrado nesta página.",
  summaryTitle: "Resumo da página",
  dictHint: "Selecione uma palavra na página para buscar sua definição.",
  dictSearching: "Buscando…",
  dictNotFound: "Nenhuma definição encontrada.",
  noDesc: "Sem descrição",
  voiceOn: "Voz ativa: diga «descer», «subir», «início», «fim», «ler», «parar»",
  voiceUnsupported: "Este navegador não suporta comandos de voz.",
  ttsUnsupported: "Este navegador não suporta síntese de voz.",
  ttsHint: "Clique em qualquer texto para ouvi-lo.",
  gridHint: "Pressione 1-9 para aproximar · Enter para clicar · Esc para sair",
  kbdHint: "H cabeçalhos · L links · B botões · G regiões",
  smartHint: "Use ← ↑ ↓ → para se mover entre elementos e Enter para ativar.",
  offNote: "A ferramenta está desligada. Pressione «Ligar» abaixo para reativar seus ajustes.",
  statementText:
    "Este site inclui uma ferramenta de preferências de acessibilidade que permite a cada visitante ajustar a apresentação (tamanho do texto, contraste, espaçamento, guias de leitura, leitura em voz alta e mais). É uma camada de conforto: complementa, mas não substitui, a acessibilidade do código e do conteúdo conforme WCAG 2.2. Se encontrar uma barreira, contate-nos pelo link «Enviar comentários».",
};

const it: Strings = {
  easyRead: "Lettura facile (IA)",
  aiSummary: "Riassunto con IA",
  aiLoading: "Generazione con IA…",
  aiError: "L'IA non è disponibile al momento.",
  title: "Accessibilità",
  open: "Apri il menu di accessibilità",
  close: "Chiudi",
  moveSide: "Sposta il pannello di lato",
  hideWidget: "Nascondi widget",
  profiles: "Profili di accessibilità",
  navigation: "Impostazioni di navigazione",
  colors: "Impostazioni colore",
  content: "Impostazioni contenuto",
  pMotor: "Motorio",
  pBlind: "Cecità",
  pColorblind: "Daltonismo",
  pDyslexia: "Dislessia",
  pLowVision: "Ipovisione",
  pCognitive: "Cognitivo",
  pEpilepsy: "Epilessia",
  pAdhd: "ADHD",
  srMode: "Lettura al focus",
  keyboardNav: "Navigazione da tastiera",
  mousegrid: "Griglia del mouse",
  smartNav: "Navigazione intelligente",
  tts: "Lettore di testo",
  voice: "Comandi vocali",
  mono: "Monocromatico",
  darkhc: "Contrasto elevato scuro",
  brighthc: "Contrasto elevato chiaro",
  lowsat: "Saturazione bassa",
  highsat: "Saturazione alta",
  contrastMode: "Modalità contrasto",
  customColor: "Colore personalizzato",
  customColorDesc: "Cambia i colori del sito",
  bgs: "Sfondi",
  headings: "Titoli",
  contents: "Contenuti",
  resetColors: "Ripristina colori",
  fontSizing: "Dimensione del carattere",
  fontSizingDesc: "Aumenta e riduci la dimensione del testo",
  fontSize: "Dimensione",
  lineSp: "Interlinea",
  wordSp: "Tra le parole",
  letterSp: "Tra le lettere",
  resetFont: "Ripristina carattere",
  cursor: "Cursore",
  cursorDesc: "Ingrandisci il cursore e cambia colore",
  cursorWhite: "BIANCO",
  cursorBlack: "NERO",
  blinks: "Blocca lampeggi",
  magnifier: "Lente d'ingrandimento",
  readableFont: "Carattere leggibile",
  imgDesc: "Descrizione immagini",
  links: "Evidenzia link",
  readableMode: "Modalità lettura",
  textMag: "Ingranditore di testo",
  pageStructure: "Struttura della pagina",
  muteMedia: "Disattiva audio",
  pageSummary: "Riepilogo della pagina",
  readFocus: "Focus di lettura",
  readGuide: "Guida alla lettura",
  dict: "Dizionario",
  vkb: "Tastiera virtuale",
  turnOff: "Spegni",
  turnOn: "Accendi",
  statement: "Dichiarazione di accessibilità",
  feedback: "Invia feedback",
  resetAll: "Ripristina tutto",
  structHeadings: "Intestazioni",
  structLandmarks: "Regioni",
  structLinks: "Link",
  emptyList: "Nessuno trovato in questa pagina.",
  summaryTitle: "Riepilogo della pagina",
  dictHint: "Seleziona una parola nella pagina per cercarne la definizione.",
  dictSearching: "Ricerca…",
  dictNotFound: "Nessuna definizione trovata.",
  noDesc: "Senza descrizione",
  voiceOn: "Voce attiva: di' «giù», «su», «inizio», «fine», «leggi», «stop»",
  voiceUnsupported: "Questo browser non supporta i comandi vocali.",
  ttsUnsupported: "Questo browser non supporta la sintesi vocale.",
  ttsHint: "Fai clic su un testo per ascoltarlo.",
  gridHint: "Premi 1-9 per ingrandire · Invio per cliccare · Esc per uscire",
  kbdHint: "H intestazioni · L link · B pulsanti · G regioni",
  smartHint: "Usa ← ↑ ↓ → per spostarti tra gli elementi e Invio per attivare.",
  offNote: "Lo strumento è spento. Premi «Accendi» qui sotto per riattivare le tue impostazioni.",
  statementText:
    "Questo sito include uno strumento di preferenze di accessibilità che consente a ogni visitatore di regolare la presentazione (dimensione del testo, contrasto, spaziatura, guide alla lettura, lettura ad alta voce e altro). È un livello di comfort: integra, ma non sostituisce, l'accessibilità del codice e dei contenuti secondo WCAG 2.2. Se trovi una barriera, contattaci tramite «Invia feedback».",
};

const zh: Strings = {
  easyRead: "易读模式（AI）",
  aiSummary: "AI 摘要",
  aiLoading: "AI 生成中…",
  aiError: "AI 暂时不可用。",
  title: "无障碍",
  open: "打开无障碍菜单",
  close: "关闭",
  moveSide: "将面板移到另一侧",
  hideWidget: "隐藏小工具",
  profiles: "无障碍配置文件",
  navigation: "导航调整",
  colors: "颜色调整",
  content: "内容调整",
  pMotor: "肢体障碍",
  pBlind: "失明",
  pColorblind: "色盲",
  pDyslexia: "阅读障碍",
  pLowVision: "低视力",
  pCognitive: "认知障碍",
  pEpilepsy: "癫痫",
  pAdhd: "多动症",
  srMode: "聚焦朗读",
  keyboardNav: "键盘导航",
  mousegrid: "鼠标网格",
  smartNav: "智能导航",
  tts: "文本朗读器",
  voice: "语音命令",
  mono: "单色",
  darkhc: "深色高对比度",
  brighthc: "浅色高对比度",
  lowsat: "低饱和度",
  highsat: "高饱和度",
  contrastMode: "对比度模式",
  customColor: "自定义颜色",
  customColorDesc: "更改网站颜色",
  bgs: "背景",
  headings: "标题",
  contents: "内容",
  resetColors: "重置颜色",
  fontSizing: "字体大小",
  fontSizingDesc: "增大和减小文字大小",
  fontSize: "大小",
  lineSp: "行距",
  wordSp: "词距",
  letterSp: "字距",
  resetFont: "重置字体",
  cursor: "光标",
  cursorDesc: "放大光标并更改颜色",
  cursorWhite: "白色",
  cursorBlack: "黑色",
  blinks: "阻止闪烁",
  magnifier: "放大镜",
  readableFont: "易读字体",
  imgDesc: "图片描述",
  links: "突出显示链接",
  readableMode: "阅读模式",
  textMag: "文字放大器",
  pageStructure: "页面结构",
  muteMedia: "静音媒体",
  pageSummary: "页面摘要",
  readFocus: "阅读焦点",
  readGuide: "阅读参考线",
  dict: "词典",
  vkb: "虚拟键盘",
  turnOff: "关闭",
  turnOn: "开启",
  statement: "无障碍声明",
  feedback: "发送反馈",
  resetAll: "全部重置",
  structHeadings: "标题",
  structLandmarks: "区域",
  structLinks: "链接",
  emptyList: "此页面未找到任何内容。",
  summaryTitle: "页面摘要",
  dictHint: "在页面上选择一个词以查找其定义。",
  dictSearching: "搜索中…",
  dictNotFound: "未找到定义。",
  noDesc: "无描述",
  voiceOn: "语音已开启：说「下」「上」「顶部」「底部」「朗读」「停止」",
  voiceUnsupported: "此浏览器不支持语音命令。",
  ttsUnsupported: "此浏览器不支持语音合成。",
  ttsHint: "点击任意文字即可收听。",
  gridHint: "按 1-9 放大 · Enter 点击 · Esc 退出",
  kbdHint: "H 标题 · L 链接 · B 按钮 · G 区域",
  smartHint: "使用 ← ↑ ↓ → 在元素之间移动，按 Enter 激活。",
  offNote: "工具已关闭。请点击下方「开启」以恢复您的设置。",
  statementText:
    "本网站包含一个无障碍偏好工具，允许每位访客调整页面呈现方式（文字大小、对比度、间距、阅读参考线、朗读等）。它是一个舒适层：它补充但不能取代符合 WCAG 2.2 的无障碍代码和内容。如果您遇到访问障碍，请通过「发送反馈」联系我们。",
};

const ja: Strings = {
  easyRead: "やさしい文章（AI）",
  aiSummary: "AI要約",
  aiLoading: "AI生成中…",
  aiError: "AIは現在利用できません。",
  title: "アクセシビリティ",
  open: "アクセシビリティメニューを開く",
  close: "閉じる",
  moveSide: "パネルを反対側に移動",
  hideWidget: "ウィジェットを非表示",
  profiles: "アクセシビリティプロファイル",
  navigation: "ナビゲーション調整",
  colors: "色の調整",
  content: "コンテンツ調整",
  pMotor: "運動機能",
  pBlind: "全盲",
  pColorblind: "色覚異常",
  pDyslexia: "ディスレクシア",
  pLowVision: "ロービジョン",
  pCognitive: "認知",
  pEpilepsy: "てんかん",
  pAdhd: "ADHD",
  srMode: "フォーカス読み上げ",
  keyboardNav: "キーボードナビゲーション",
  mousegrid: "マウスグリッド",
  smartNav: "スマートナビゲーション",
  tts: "テキストリーダー",
  voice: "音声コマンド",
  mono: "モノクロ",
  darkhc: "ダークハイコントラスト",
  brighthc: "ライトハイコントラスト",
  lowsat: "低彩度",
  highsat: "高彩度",
  contrastMode: "コントラストモード",
  customColor: "カスタムカラー",
  customColorDesc: "サイトの色を変更",
  bgs: "背景",
  headings: "見出し",
  contents: "本文",
  resetColors: "色をリセット",
  fontSizing: "フォントサイズ",
  fontSizingDesc: "文字サイズを拡大・縮小",
  fontSize: "サイズ",
  lineSp: "行間",
  wordSp: "単語間隔",
  letterSp: "文字間隔",
  resetFont: "フォントをリセット",
  cursor: "カーソル",
  cursorDesc: "カーソルを拡大して色を変更",
  cursorWhite: "白",
  cursorBlack: "黒",
  blinks: "点滅をブロック",
  magnifier: "拡大鏡",
  readableFont: "読みやすいフォント",
  imgDesc: "画像の説明",
  links: "リンクを強調",
  readableMode: "リーディングモード",
  textMag: "テキスト拡大",
  pageStructure: "ページ構造",
  muteMedia: "メディアをミュート",
  pageSummary: "ページ概要",
  readFocus: "読書フォーカス",
  readGuide: "読書ガイド",
  dict: "辞書",
  vkb: "仮想キーボード",
  turnOff: "オフにする",
  turnOn: "オンにする",
  statement: "アクセシビリティ方針",
  feedback: "フィードバックを送信",
  resetAll: "すべてリセット",
  structHeadings: "見出し",
  structLandmarks: "ランドマーク",
  structLinks: "リンク",
  emptyList: "このページには見つかりませんでした。",
  summaryTitle: "ページ概要",
  dictHint: "ページ上の単語を選択すると定義を検索します。",
  dictSearching: "検索中…",
  dictNotFound: "定義が見つかりません。",
  noDesc: "説明なし",
  voiceOn: "音声オン：「下」「上」「先頭」「末尾」「読む」「停止」と言ってください",
  voiceUnsupported: "このブラウザは音声コマンドに対応していません。",
  ttsUnsupported: "このブラウザは音声合成に対応していません。",
  ttsHint: "テキストをクリックすると読み上げます。",
  gridHint: "1-9 でズーム · Enter でクリック · Esc で終了",
  kbdHint: "H 見出し · L リンク · B ボタン · G ランドマーク",
  smartHint: "← ↑ ↓ → で要素間を移動し、Enter で実行します。",
  offNote: "ツールはオフです。下の「オンにする」を押すと設定が復元されます。",
  statementText:
    "このサイトには、訪問者が表示（文字サイズ、コントラスト、間隔、読書ガイド、読み上げなど）を調整できるアクセシビリティ設定ツールが含まれています。これは快適さのためのレイヤーであり、WCAG 2.2 に準拠したコードとコンテンツのアクセシビリティを補完するものであって、置き換えるものではありません。障壁を見つけた場合は「フィードバックを送信」からご連絡ください。",
};

const ar: Strings = {
  easyRead: "قراءة سهلة (ذكاء اصطناعي)",
  aiSummary: "ملخص بالذكاء الاصطناعي",
  aiLoading: "جارٍ الإنشاء بالذكاء الاصطناعي…",
  aiError: "الذكاء الاصطناعي غير متاح الآن.",
  title: "إمكانية الوصول",
  open: "فتح قائمة إمكانية الوصول",
  close: "إغلاق",
  moveSide: "نقل اللوحة إلى الجانب الآخر",
  hideWidget: "إخفاء الأداة",
  profiles: "ملفات إمكانية الوصول",
  navigation: "إعدادات التنقل",
  colors: "إعدادات الألوان",
  content: "إعدادات المحتوى",
  pMotor: "حركي",
  pBlind: "كفيف",
  pColorblind: "عمى الألوان",
  pDyslexia: "عسر القراءة",
  pLowVision: "ضعف البصر",
  pCognitive: "إدراكي",
  pEpilepsy: "الصرع",
  pAdhd: "فرط الحركة",
  srMode: "القراءة عند التركيز",
  keyboardNav: "التنقل بلوحة المفاتيح",
  mousegrid: "شبكة الفأرة",
  smartNav: "التنقل الذكي",
  tts: "قارئ النصوص",
  voice: "الأوامر الصوتية",
  mono: "أحادي اللون",
  darkhc: "تباين عالٍ داكن",
  brighthc: "تباين عالٍ فاتح",
  lowsat: "تشبع منخفض",
  highsat: "تشبع عالٍ",
  contrastMode: "وضع التباين",
  customColor: "لون مخصص",
  customColorDesc: "غيّر ألوان الموقع",
  bgs: "الخلفيات",
  headings: "العناوين",
  contents: "المحتوى",
  resetColors: "إعادة تعيين الألوان",
  fontSizing: "حجم الخط",
  fontSizingDesc: "تكبير وتصغير حجم النص",
  fontSize: "الحجم",
  lineSp: "تباعد الأسطر",
  wordSp: "تباعد الكلمات",
  letterSp: "تباعد الأحرف",
  resetFont: "إعادة تعيين الخط",
  cursor: "المؤشر",
  cursorDesc: "تكبير المؤشر وتغيير لونه",
  cursorWhite: "أبيض",
  cursorBlack: "أسود",
  blinks: "حظر الوميض",
  magnifier: "المكبر",
  readableFont: "خط مقروء",
  imgDesc: "وصف الصور",
  links: "إبراز الروابط",
  readableMode: "وضع القراءة",
  textMag: "مكبر النص",
  pageStructure: "بنية الصفحة",
  muteMedia: "كتم الوسائط",
  pageSummary: "ملخص الصفحة",
  readFocus: "تركيز القراءة",
  readGuide: "دليل القراءة",
  dict: "القاموس",
  vkb: "لوحة مفاتيح افتراضية",
  turnOff: "إيقاف",
  turnOn: "تشغيل",
  statement: "بيان إمكانية الوصول",
  feedback: "إرسال ملاحظات",
  resetAll: "إعادة تعيين الكل",
  structHeadings: "العناوين",
  structLandmarks: "المناطق",
  structLinks: "الروابط",
  emptyList: "لم يتم العثور على شيء في هذه الصفحة.",
  summaryTitle: "ملخص الصفحة",
  dictHint: "حدد كلمة في الصفحة للبحث عن تعريفها.",
  dictSearching: "جارٍ البحث…",
  dictNotFound: "لم يتم العثور على تعريف.",
  noDesc: "بدون وصف",
  voiceOn: "الصوت مفعّل: قل «انزل»، «اطلع»، «البداية»، «النهاية»، «اقرأ»، «توقف»",
  voiceUnsupported: "هذا المتصفح لا يدعم الأوامر الصوتية.",
  ttsUnsupported: "هذا المتصفح لا يدعم تحويل النص إلى كلام.",
  ttsHint: "انقر على أي نص للاستماع إليه.",
  gridHint: "اضغط 1-9 للتكبير · Enter للنقر · Esc للخروج",
  kbdHint: "H العناوين · L الروابط · B الأزرار · G المناطق",
  smartHint: "استخدم ← ↑ ↓ → للتنقل بين العناصر و Enter للتفعيل.",
  offNote: "الأداة متوقفة. اضغط «تشغيل» أدناه لإعادة تفعيل إعداداتك.",
  statementText:
    "يتضمن هذا الموقع أداة تفضيلات إمكانية الوصول تتيح لكل زائر ضبط العرض (حجم النص، التباين، التباعد، أدلة القراءة، القراءة الصوتية والمزيد). إنها طبقة راحة: تكمّل، لكنها لا تحل محل، إمكانية وصول الكود والمحتوى وفق WCAG 2.2. إذا واجهت عائقًا، تواصل معنا عبر «إرسال ملاحظات».",
};

const ru: Strings = {
  easyRead: "Простой текст (ИИ)",
  aiSummary: "Сводка ИИ",
  aiLoading: "ИИ генерирует…",
  aiError: "ИИ сейчас недоступен.",
  title: "Доступность",
  open: "Открыть меню доступности",
  close: "Закрыть",
  moveSide: "Переместить панель на другую сторону",
  hideWidget: "Скрыть виджет",
  profiles: "Профили доступности",
  navigation: "Настройки навигации",
  colors: "Настройки цвета",
  content: "Настройки контента",
  pMotor: "Моторика",
  pBlind: "Слепота",
  pColorblind: "Дальтонизм",
  pDyslexia: "Дислексия",
  pLowVision: "Слабое зрение",
  pCognitive: "Когнитивный",
  pEpilepsy: "Эпилепсия",
  pAdhd: "СДВГ",
  srMode: "Чтение при фокусе",
  keyboardNav: "Навигация с клавиатуры",
  mousegrid: "Сетка мыши",
  smartNav: "Умная навигация",
  tts: "Чтение текста",
  voice: "Голосовые команды",
  mono: "Монохром",
  darkhc: "Тёмный высокий контраст",
  brighthc: "Светлый высокий контраст",
  lowsat: "Низкая насыщенность",
  highsat: "Высокая насыщенность",
  contrastMode: "Режим контраста",
  customColor: "Свой цвет",
  customColorDesc: "Изменить цвета сайта",
  bgs: "Фон",
  headings: "Заголовки",
  contents: "Содержимое",
  resetColors: "Сбросить цвета",
  fontSizing: "Размер шрифта",
  fontSizingDesc: "Увеличение и уменьшение размера текста",
  fontSize: "Размер",
  lineSp: "Межстрочный интервал",
  wordSp: "Между словами",
  letterSp: "Между буквами",
  resetFont: "Сбросить шрифт",
  cursor: "Курсор",
  cursorDesc: "Увеличить курсор и изменить его цвет",
  cursorWhite: "БЕЛЫЙ",
  cursorBlack: "ЧЁРНЫЙ",
  blinks: "Блокировать мигание",
  magnifier: "Лупа",
  readableFont: "Читаемый шрифт",
  imgDesc: "Описание изображений",
  links: "Выделить ссылки",
  readableMode: "Режим чтения",
  textMag: "Увеличитель текста",
  pageStructure: "Структура страницы",
  muteMedia: "Отключить звук медиа",
  pageSummary: "Сводка страницы",
  readFocus: "Фокус чтения",
  readGuide: "Линейка для чтения",
  dict: "Словарь",
  vkb: "Виртуальная клавиатура",
  turnOff: "Выключить",
  turnOn: "Включить",
  statement: "Заявление о доступности",
  feedback: "Отправить отзыв",
  resetAll: "Сбросить всё",
  structHeadings: "Заголовки",
  structLandmarks: "Области",
  structLinks: "Ссылки",
  emptyList: "На этой странице ничего не найдено.",
  summaryTitle: "Сводка страницы",
  dictHint: "Выделите слово на странице, чтобы найти его определение.",
  dictSearching: "Поиск…",
  dictNotFound: "Определение не найдено.",
  noDesc: "Без описания",
  voiceOn: "Голос включён: скажите «вниз», «вверх», «начало», «конец», «читать», «стоп»",
  voiceUnsupported: "Этот браузер не поддерживает голосовые команды.",
  ttsUnsupported: "Этот браузер не поддерживает синтез речи.",
  ttsHint: "Нажмите на любой текст, чтобы услышать его.",
  gridHint: "Нажмите 1-9 для увеличения · Enter — клик · Esc — выход",
  kbdHint: "H заголовки · L ссылки · B кнопки · G области",
  smartHint: "Используйте ← ↑ ↓ → для перемещения между элементами и Enter для активации.",
  offNote: "Инструмент выключен. Нажмите «Включить» ниже, чтобы вернуть ваши настройки.",
  statementText:
    "Этот сайт включает инструмент настроек доступности, который позволяет каждому посетителю настроить отображение (размер текста, контраст, интервалы, линейки для чтения, озвучивание и другое). Это слой комфорта: он дополняет, но не заменяет доступность кода и контента по WCAG 2.2. Если вы столкнулись с барьером, свяжитесь с нами через «Отправить отзыв».",
};

const dictionaries: Record<Lang, Strings> = { es, en, fr, de, pt, it, zh, ja, ar, ru };

export const LANGS = Object.keys(dictionaries) as Lang[];

export function getStrings(lang: Lang): Strings {
  const d = dictionaries[lang];
  // El español es el idioma base: cubre cualquier clave que falte.
  return d && d !== es ? { ...es, ...d } : es;
}
